// WHAT: Main hook for time tracking control with session management and event handling
// WHY:  Provides unified interface for starting/stopping tracking with session persistence and event listeners
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: core.invoke, listen from Tauri API, debugProductivity, localStorage
// RISKS:
//   - NOTE: Session persistence uses localStorage - may be out of sync with backend
//   - NOTE: Debouncing may cause delays in UI updates
//   - NOTE: Event listeners may not clean up properly on errors
//   - NOTE: beforeunload handler may not work in all browsers

import { core } from "@tauri-apps/api";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { useState, useCallback, useEffect, useRef } from "react";

import { debugProductivity } from "@/domains/logging/utils/debugLog";

/**
 * Tracking session interface
 */
export interface TrackingSession {
  /** Aspect name */
  aspect: string;
  /** Creation timestamp */
  created_at: string;
  /** Efficiency rating (1-5) */
  efficiency_rating?: null | number;
  /** Session end time */
  end_time?: null | string;
  /** Session identifier */
  id: string;
  /** Impact score */
  impact_score?: null | string;
  /** Session notes */
  notes?: null | string;
  /** Session start time */
  start_time: string;
  /** Associated task ID */
  task_id?: null | string;
  /** What was inefficient */
  what_was_inefficient?: null | string;
  /** What went well */
  what_went_well?: null | string;
  /** Workspace name */
  workspace: string;
}

/**
 * WHAT:
 *   Main hook that provides time tracking control with session management,
 *   event listeners, and persistence.
 *
 * WHY:
 *   Centralizes all tracking logic including start/stop operations, session
 *   restoration, event handling, and cleanup.
 *
 * BEHAVIOR:
 *   - Manages tracking status and session state
 *   - Restores session from localStorage on mount
 *   - Provides debounced start/stop functions
 *   - Listens to Tauri events (tracking_stopped, close-requested)
 *   - Handles browser beforeunload event
 *   - Persists session to localStorage
 *   - Sets buzzer color on start
 *
 * RISKS:
 *   - NOTE: Session persistence uses localStorage - may be out of sync with backend
 *   - NOTE: Debouncing may cause delays in UI updates
 *   - NOTE: Event listeners may not clean up properly on errors
 *   - NOTE: beforeunload handler may not work in all browsers
 *
 * @returns Time tracking control state and functions
 *
 */
export function useTimeTracqControl() {
  /* ---------- State ---------- */
  const [status, setStatus] = useState<
    | "Already tracking"
    | "Error starting tracking"
    | "Error stopping tracking"
    | "Idle"
    | "No active session"
    | "Starting…"
    | "Stopping…"
    | "Tracking restored"
    | "Tracking started"
    | "Tracking stopped"
  >("Idle");
  const [session, setSession] = useState<null | TrackingSession>(null);

  // NOTE: Promise queue for sequential action execution
  const actionQueue = useRef<Promise<void>>(Promise.resolve());

  /**
   * WHAT:
   *   Debounces function execution to prevent rapid successive calls.
   *
   * WHY:
   *   Prevents race conditions and excessive backend calls.
   *
   * BEHAVIOR:
   *   - Checks if enough time has passed since last action
   *   - Queues function execution sequentially
   *   - Ignores calls within delay period
   */
  const lastActionTime = useRef<number>(0);
  const runDebounced = useCallback((function_: () => Promise<void>, delay: number) => {
    const now = Date.now();
    if (now - lastActionTime.current < delay) {
      return;
    }
    lastActionTime.current = now;
    actionQueue.current = actionQueue.current.then(function_).catch(() => {
      // NOTE: Error in queued action - handled silently
    });
  }, []);

  // NOTE: Session restore from localStorage on mount
  useEffect(() => {
    debugProductivity("info", "timetracq", "Checking for existing session", {});

    const raw = localStorage.getItem("timetracq_session");
    if (!raw) {
      debugProductivity("info", "timetracq", "No existing session found", {});
      return;
    }

    const parsed = JSON.parse(raw) as TrackingSession;
    debugProductivity("info", "timetracq", "Found existing session, checking if active", {
      aspect: parsed.aspect,
      session_id: parsed.id,
      workspace: parsed.workspace,
    });

    /**
     * WHAT:
     *   Handles active session restoration.
     *
     * @param sessionData - Session data to restore
     *
     */
    const handleActiveSession = (sessionData: TrackingSession) => {
      debugProductivity("info", "timetracq", "Session is active, restoring", {
        session_id: sessionData.id,
      });
      setSession(sessionData);
      setStatus("Tracking restored");
    };

    /**
     * WHAT:
     *   Handles inactive session cleanup.
     *
     * @param sessionId - Session ID to clear
     *
     */
    const handleInactiveSession = (sessionId: string) => {
      debugProductivity("info", "timetracq", "Session not active, clearing", {
        session_id: sessionId,
      });
      localStorage.removeItem("timetracq_session");
      setSession(null);
      setStatus("Idle");
    };

    core
      .invoke<boolean>("is_tracking_active")
      .then((active) => {
        // eslint-disable-next-line sonarjs/no-selector-parameter -- Reason: Boolean parameter is necessary to determine which handler to call
        if (active) {
          handleActiveSession(parsed);
        } else {
          handleInactiveSession(parsed.id);
        }
      })
      .catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugProductivity("error", "timetracq", "Error checking session status", {
          error: errorMessage,
          session_id: parsed.id,
        });
        localStorage.removeItem("timetracq_session");
        setSession(null);
        setStatus("Idle");
      });
  }, []);

  /**
   * WHAT:
   *   Stops active tracking session with optional notes.
   *
   * WHY:
   *   Completes tracking session and saves end time and notes.
   *
   * BEHAVIOR:
   *   - Validates active session
   *   - Updates session with end time and notes
   *   - Clears session state and localStorage
   *   - Handles errors gracefully
   */
  const stopTracking = useCallback(
    async (notes: null | string = null) => {
      if (!session) {
        debugProductivity("warn", "timetracq", "Stop tracking called but no active session", {});
        setStatus("No active session");
        return;
      }

      debugProductivity("info", "timetracq", "Stopping tracking session", {
        aspect: session.aspect,
        has_notes: !!notes,
        session_id: session.id,
        workspace: session.workspace,
      });

      setStatus("Stopping…");

      try {
        const updateResult = await core.invoke("update_tracking_session", {
          endTime: new Date().toISOString(),
          id: session.id,
          notes,
        });

        debugProductivity("info", "timetracq", "Tracking session stopped successfully", {
          result: updateResult,
          session_id: session.id,
        });

        setSession(null);
        setStatus("Tracking stopped");
        localStorage.removeItem("timetracq_session");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugProductivity("error", "timetracq", "Error stopping tracking session", {
          error: errorMessage,
          session_id: session.id,
        });
        setStatus("Error stopping tracking");
        setSession(null);
        localStorage.removeItem("timetracq_session");
      }
    },
    [session],
  );

  // NOTE: Event listeners for tracking events and app lifecycle
  useEffect(() => {
    let unlistenTracking: null | UnlistenFn = null;
    let unlistenClose: null | UnlistenFn = null;

    const setupListeners = async () => {
      try {
        // NOTE: Tauri Event for tracking_stopped
        unlistenTracking = await listen("tracking_stopped", (event) => {
          const sessionId = event.payload;
          debugProductivity("info", "timetracq", "Received tracking_stopped event", {
            session_id: sessionId,
          });
          setSession(null);
          setStatus("Tracking stopped");
          localStorage.removeItem("timetracq_session");
        });

        // NOTE: Tauri Close Event - stop tracking before app closes
        unlistenClose = await listen("tauri://close-requested", () => {
          if (session) {
            void stopTracking("Session forced stop");
          }
        });
      } catch {
        // NOTE: Error setting up event listeners
      }
    };

    void setupListeners();

    // NOTE: Browser beforeunload Event - stop tracking before page unload
    const handleBeforeUnload = () => {
      if (session) {
        void stopTracking("Session forced stop");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // NOTE: Cleanup event listeners
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (unlistenTracking && typeof unlistenTracking === "function") {
        try {
          unlistenTracking();
        } catch {
          // NOTE: Error during cleanup
        }
      }
      if (unlistenClose && typeof unlistenClose === "function") {
        try {
          unlistenClose();
        } catch {
          // NOTE: Error during cleanup
        }
      }
    };
  }, [session, stopTracking]);

  /**
   * WHAT:
   *   Starts new tracking session with workspace and aspect.
   *
   * WHY:
   *   Initiates time tracking and persists session state.
   *
   * BEHAVIOR:
   *   - Validates no active session
   *   - Creates session payload with UUID
   *   - Calls backend to start tracking
   *   - Sets session state and localStorage
   *   - Sets buzzer color if provided
   *   - Handles errors gracefully
   */
  const startTracking = useCallback(
    async (workspace: string, aspect: string, color?: string) => {
      debugProductivity("info", "timetracq", "startTracking called", {
        aspect,
        current_status: status,
        has_color: !!color,
        has_existing_session: !!session,
        workspace,
      });

      if (session) {
        debugProductivity("warn", "timetracq", "Already tracking, ignoring startTracking", {
          existing_session_id: session.id,
        });
        setStatus("Already tracking");
        return;
      }
      setStatus("Starting…");

      const payload = {
        aspect,
        createdAt: new Date().toISOString(),
        id: crypto.randomUUID(),
        startTime: new Date().toISOString(),
        workspace,
      };

      try {
        debugProductivity("info", "timetracq", "Calling backend start_tracking command", {
          aspect: payload.aspect,
          payload_id: payload.id,
          workspace: payload.workspace,
        });

        // NOTE: First start tracking
        const trackingResult = await core.invoke<TrackingSession>("start_tracking", payload);

        debugProductivity("info", "timetracq", "Backend start_tracking returned successfully", {
          aspect: trackingResult.aspect,
          session_id: trackingResult.id,
          workspace: trackingResult.workspace,
        });

        // NOTE: Set session immediately after successful tracking start
        setSession(trackingResult);
        setStatus("Tracking started");
        localStorage.setItem("timetracq_session", JSON.stringify(trackingResult));

        debugProductivity("info", "timetracq", "Tracking started successfully - session set", {
          aspect: trackingResult.aspect,
          is_tracking_now: true,
          session_id: trackingResult.id,
          workspace: trackingResult.workspace,
        });

        // NOTE: Then set buzzer color (non-blocking)
        if (color) {
          core.invoke("set_buzzer_color", { color }).catch((error: unknown) => {
            const errorMessage = error instanceof Error ? error.message : String(error);
            debugProductivity("warn", "timetracq", "Failed to set buzzer color", {
              error: errorMessage,
            });
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugProductivity("error", "timetracq", "Error starting tracking", {
          aspect,
          error: errorMessage,
          workspace,
        });
        setStatus("Error starting tracking");
        // Don't set session on error
      }
    },
    [session, status],
  );

  // NOTE: Public API with debounced functions
  return {
    isTracking: session !== null,
    session,
    // NOTE: Debounced start tracking (80ms delay)
    startTracking: (ws: string, asp: string, col?: string) => {
      runDebounced(() => startTracking(ws, asp, col), 80);
    },
    status,
    // NOTE: Debounced stop tracking (80ms delay)
    stopTracking: (notes?: string) => {
      runDebounced(() => stopTracking(notes ?? null), 80);
    },
  };
}
